import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { post, put } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { getEnumValues } from "@common/enumOperations";
import { useValidator } from "@common/hooks/validation";
import { useDotLegalMutation, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { PolicyType } from "../policies/Policies.types";
import { PolicyViewModel } from "../policyOverview/PolicyOverview.types";
import { PolicyPaneFormProps, PolicySaveModel } from "./AddEditPolicyPane.types";
import { useUrlProvider } from "../../useUrlProvider";
import { usePanes } from "../../main/panes/usePanes";
import { usePane } from "../../main/panes/paneLayout/usePane";

export function useAddEditPolicyPane(props: PolicyPaneFormProps) {
    const snackbar = useDotLegalSnackbar();
    const navigate = useNavigate();
    const queryClient = useDotLegalQueryClient();
    const { translatePolicyTypes, translateString } = useTranslation();
    const { getPolicyUrl } = useUrlProvider();
    const { close, setHasUnsavedChanges } = usePane();
    const { refetchPageBehindPanes } = usePanes();
    const isEdit = props.policyId !== undefined;
    const { addonsFeatures } = usePlanContext();
    const hasPolicyManagement = addonsFeatures.policyManagement;
    const [saveModel, setSaveModel] = useState<PolicySaveModel>(props.policy ? policyViewModelToSaveModel(props.policy) : getDefaultPolicy());

    function onModelChange(next: PolicySaveModel) {
        setHasUnsavedChanges(true);
        setSaveModel(next);
    }

    const validation = useValidator<PolicySaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            type: validators.validateNotEmpty((item) => item.type, translateString("type")),
            responsibleId: validators.validateNotEmpty((item) => item.responsibleId, translateString("responsible")),
            approverId: validators.validateNotEmpty(
                (item) => item.approverId,
                translateString("approver"),
                () => !hasPolicyManagement
            ),
        }),
        [hasPolicyManagement]
    );

    function create() {
        return post<{ id: string }>("/policies", saveModel);
    }

    const createMutation = useDotLegalMutation(create, {
        onSuccess: (response: Result<{ id: string }>) => {
            if (response.success()) {
                snackbar.show(createElement(Trans, { i18nKey: "policyCreated", values: { name: saveModel.name } }));
                // A pane outlives a navigation, so it has to close itself or it stays open over the new policy
                close();
                navigate(getPolicyUrl(response.value().id));
            } else {
                snackbar.show(translateString("policyExists"), "warning");
            }
        },
    });

    function update() {
        return put<string>("/policies/" + props.policyId, saveModel);
    }

    const editMutation = useDotLegalMutation(update, {
        onSuccess: (response: Result<string>) => {
            if (response.success()) {
                queryClient.refetchQueries(`/policies/${props.policyId}`);
                refetchPageBehindPanes();
                close();
                snackbar.show(translateString("policyUpdated"));
            } else {
                snackbar.show(translateString("policyExists"), "warning");
            }
        },
    });

    const savePolicy = async () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (isEdit) {
            await editMutation.mutateAsync();
        } else {
            await createMutation.mutateAsync();
        }
    };

    const typeOptions = getEnumValues(PolicyType)
        .map((x) => ({ id: x.toString(), name: translatePolicyTypes(x.toString()) }))
        .sort((a, b) => {
            if (a.id === PolicyType.Other.toString()) return 1;
            if (b.id === PolicyType.Other.toString()) return -1;
            return a.name.localeCompare(b.name);
        });

    return {
        saveModel,
        onModelChange,
        savePolicy,
        validation,
        typeOptions,
        isEdit,
        isSaving: createMutation.isLoading || editMutation.isLoading,
    };
}

function getDefaultPolicy(): PolicySaveModel {
    return {
        name: "",
        description: "",
        type: undefined,
        responsibleId: undefined,
        approverId: undefined,
        tags: [],
    };
}

function policyViewModelToSaveModel(policy: PolicyViewModel): PolicySaveModel {
    return {
        name: policy.name,
        description: policy.description ?? "",
        type: policy.type,
        responsibleId: policy.responsible?.id ?? null,
        approverId: policy.approver?.id ?? null,
        tags: policy.tags.map((tag) => tag.id),
    };
}
